import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { isNone, isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { IdentityProvider } from 'cio/models/sso-configuration';

import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import SsoService from 'cio/services/sso';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import UserModel from 'cio/models/user';
import SsoConfigurationModel from 'cio/models/sso-configuration';
import FormValidations from 'cio/utils/form-validations';
import DS from 'ember-data';
import { tracked } from '@glimmer/tracking';

const DEFAULT_FORM_VALUES: {
  oktaOrganizationUrl: string;
  azureConfigurationUrl: string;
  genericConfigurationUrl: string;
  clientId: string;
  clientSecret: string;
} = {
  oktaOrganizationUrl: '',
  azureConfigurationUrl: '',
  genericConfigurationUrl: '',
  clientId: '',
  clientSecret: '',
};

const oidcSSOValidations = {
  oktaOrganizationUrl(this: SettingsSecuritySSO, value: string) {
    if (this.ssoConfiguration?.identityProvider !== IdentityProvider.OktaOIDC) {
      // only validate for Okta:
      return true;
    }

    if (isNone(value) || isEmpty(value.trim())) {
      return 'Okta organization URL must be provided';
    }

    try {
      new URL(value);
    } catch (e) {
      return 'Okta organization URL must have a valid format';
    }

    return true;
  },

  azureConfigurationUrl(this: SettingsSecuritySSO, value: string) {
    if (this.ssoConfiguration?.identityProvider !== IdentityProvider.AzureOIDC) {
      // only validate for Azure:
      return true;
    }

    if (isNone(value) || isEmpty(value.trim())) {
      return 'OpenID configuration document URL must be provided';
    }

    try {
      let url = new URL(value);

      if (!url.pathname.endsWith('/v2.0/.well-known/openid-configuration')) {
        throw new Error();
      }
    } catch (e) {
      return 'Azure OpenID configuration document URL must have a valid format';
    }

    return true;
  },

  genericConfigurationUrl(this: SettingsSecuritySSO, value: string) {
    if (this.ssoConfiguration?.identityProvider !== IdentityProvider.GenericOIDC) {
      // only validate for Generic:
      return true;
    }

    if (isNone(value) || isEmpty(value.trim())) {
      return 'OpenID configuration document URL must be provided';
    }

    try {
      new URL(value);
    } catch (e) {
      return 'OpenID configuration document URL must have a valid format';
    }

    return true;
  },

  clientId(value: string) {
    if (isNone(value) || isEmpty(value.trim())) {
      return 'Client ID must be provided';
    }

    return true;
  },

  clientSecret(value: string) {
    if (isNone(value) || isEmpty(value.trim())) {
      return 'Client secret must be provided';
    }

    return true;
  },
};

export default class SettingsSecuritySSO extends Controller {
  @service auth!: AuthService;
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service sso!: SsoService;
  @service store!: DS.Store;

  model!: { users: UserModel[] };

  // uncommitted ED record, set only when enabling SSO:
  @tracked newSSOConfiguration: SsoConfigurationModel | null = null;

  // edited sso configuration: either new or existing (if enabled)
  get ssoConfiguration() {
    return this.newSSOConfiguration ?? this.sso.ssoConfiguration;
  }

  @tracked showDisableSSOConfirmationModal = false;
  @tracked selectedSSOProvider: IdentityProvider | null = null;

  oidcValidations: FormValidations;
  @tracked oidcFormValues = { ...DEFAULT_FORM_VALUES };

  @tracked updateClientSecret = false;

  // constant for template usage:
  IdentityProvider = IdentityProvider;

  constructor() {
    super(...arguments);

    this.oidcValidations = FormValidations.create({
      validations: oidcSSOValidations,
      context: this,
      modelKey: 'oidcFormValues',
    });
  }

  get hasEditAccountPermission() {
    return this.auth.hasPermission('account/edit');
  }

  get readOnlyClientSecret() {
    if (this.updateClientSecret) {
      return false;
    }

    return this.sso.isEnabled;
  }

  @computed(
    'auth.user.permissions',
    'masq.{isOn,readOnly}',
    'oidcValidations.hasErrors',
    'setupSSOOktaOIDCTask.isRunning',
    'setupSSOGenericOIDCTask.isRunning',
    'setupSSOAzureOIDCTask.isRunning',
    'account.current.{readOnly,twoFactorAuthenticationRequired}',
  )
  get isOIDCAuthenticateDisabled() {
    return (
      !this.auth.hasPermission('account/edit') ||
      this.oidcValidations.hasErrors ||
      this.setupSSOOktaOIDCTask.isRunning ||
      this.setupSSOAzureOIDCTask.isRunning ||
      this.setupSSOGenericOIDCTask.isRunning ||
      this.account.current.twoFactorAuthenticationRequired
    );
  }

  setDefaults() {
    this.newSSOConfiguration = null;
    this.updateClientSecret = false;
    this.selectedSSOProvider = this.sso.ssoConfiguration?.identityProvider ?? null;
    this.showDisableSSOConfirmationModal = false;

    this.oidcFormValues = { ...DEFAULT_FORM_VALUES };
    this.oidcValidations.clearErrors();
  }

  @computed('model.users.@each.emailDomain', 'sso.ssoConfiguration.identityProvider')
  get hasTeamMembersWithGoogleSSODomainMismatch() {
    if (this.sso.ssoConfiguration == null || isEmpty(this.sso.ssoConfiguration.oidcGoogleHostedDomain)) {
      return false;
    }

    return this.model.users.toArray().some((user: UserModel) => {
      return user.emailDomain !== this.sso.ssoConfiguration?.oidcGoogleHostedDomain;
    });
  }

  setupSSOGoogleOIDCTask = task(async () => {
    if (this.newSSOConfiguration == null) {
      return;
    }

    this.analytics.track('account', 'security_sso_settings_authenticate_submitted', {
      identityProvider: this.selectedSSOProvider as string,
    });

    try {
      await this.sso.setupGoogleOIDC(this.newSSOConfiguration);

      this.alerts.success(
        'sso_setup.success.google',
        'Your G Suite account has been authenticated! Your team members will now be required to sign in with Google.',
      );
    } catch (err) {
      this.analytics.track('account', 'security_sso_settings_authenticate_failed', {
        identityProvider: this.selectedSSOProvider as string,
      });

      this.alerts.error(
        'sso_setup.error.google',
        'Something went wrong when authenticating with your Google account, please try again.',
      );

      throw err;
    }
  });

  setupSSOOktaOIDCTask = task(async (submitEvent: Event) => {
    submitEvent.preventDefault();

    // for TypeScript:
    if (this.newSSOConfiguration == null) {
      return;
    }

    try {
      await this.oidcValidations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    this.analytics.track('account', 'security_sso_settings_authenticate_submitted', {
      identityProvider: this.selectedSSOProvider as string,
    });

    this.newSSOConfiguration.setProperties({
      oidcIssuerUrl: this.oidcFormValues.oktaOrganizationUrl,
      oidcClientId: this.oidcFormValues.clientId,
      oidcClientSecret: this.oidcFormValues.clientSecret,
    });

    try {
      await this.sso.setupOktaOIDC(this.newSSOConfiguration);
      this.newSSOConfiguration = null;

      this.alerts.success(
        'sso_setup.success.okta',
        'Your account is now set up with Okta SSO. Your team members will now be required to sign in with Okta.',
      );
    } catch (err) {
      this.analytics.track('account', 'security_sso_settings_authenticate_failed', {
        identityProvider: this.selectedSSOProvider as string,
      });

      this.alerts.error(
        'sso_setup.error.okta',
        'Something went wrong when authenticating with your Okta account, please try again.',
      );
    }
  });

  setupSSOAzureOIDCTask = task(async (updateExistingConfig: boolean, event: Event) => {
    event.preventDefault();

    if (updateExistingConfig) {
      if (this.sso.ssoConfiguration == null) {
        return;
      }

      // update form values when updating an existing config
      this.oidcFormValues.azureConfigurationUrl = this.sso.ssoConfiguration.oidcConfigurationUrl;
      this.oidcFormValues.clientId = this.sso.ssoConfiguration.oidcClientId;
    }

    try {
      await this.oidcValidations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    this.analytics.track('account', 'security_sso_settings_authenticate_submitted', {
      identityProvider: this.selectedSSOProvider as string,
    });

    let ssoConfiguration = this.getSSOConfigToCreateOrUpdate(updateExistingConfig);
    // for typescript
    if (ssoConfiguration == null) {
      return;
    }

    try {
      await this.sso.setupAzureOIDC(ssoConfiguration);
      this.newSSOConfiguration = null;
      this.updateClientSecret = false;

      this.alerts.success(
        'sso_setup.success.azure',
        'Your account is now set up with Azure Active Directory SSO. Your team members will now be required to sign in with Azure Active Directory.',
      );
    } catch (err) {
      this.analytics.track('account', 'security_sso_settings_authenticate_failed', {
        identityProvider: this.selectedSSOProvider as string,
      });

      this.alerts.error(
        'sso_setup.error.azure',
        'Something went wrong when authenticating with your Azure account, please try again.',
      );
    }
  });

  setupSSOGenericOIDCTask = task(async (updateExistingConfig: boolean, event: Event) => {
    event.preventDefault();

    if (updateExistingConfig) {
      if (this.sso.ssoConfiguration == null) {
        return;
      }

      // update form values when updating an existing config
      this.oidcFormValues.genericConfigurationUrl = this.sso.ssoConfiguration.oidcConfigurationUrl;
      this.oidcFormValues.clientId = this.sso.ssoConfiguration.oidcClientId;
    }

    try {
      await this.oidcValidations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    this.analytics.track('account', 'security_sso_settings_authenticate_submitted', {
      identityProvider: this.selectedSSOProvider as string,
    });

    let ssoConfiguration = this.getSSOConfigToCreateOrUpdate(updateExistingConfig);
    // for typescript
    if (ssoConfiguration == null) {
      return;
    }

    try {
      await this.sso.setupGenericOIDC(ssoConfiguration);
      this.newSSOConfiguration = null;
      this.updateClientSecret = false;

      this.alerts.success(
        'sso_setup.success.generic_oidc',
        'Your account is now set up with SSO. Your team members will now be required to sign in with your chosen OpenID Connect.',
      );
    } catch (err) {
      this.analytics.track('account', 'security_sso_settings_authenticate_failed', {
        identityProvider: this.selectedSSOProvider as string,
      });

      this.alerts.error(
        'sso_setup.error.generic_oidc',
        'Something went wrong when authenticating with your SSO account, please try again.',
      );
    }
  });

  getSSOConfigToCreateOrUpdate(updateExistingConfig: boolean) {
    let ssoConfiguration: SsoConfigurationModel;

    if (updateExistingConfig) {
      if (this.sso.ssoConfiguration == null) {
        return;
      }

      ssoConfiguration = this.sso.ssoConfiguration;
      ssoConfiguration.oidcClientSecret = this.oidcFormValues.clientSecret;
    } else {
      if (this.newSSOConfiguration == null) {
        return;
      }

      if (this.selectedSSOProvider === IdentityProvider.AzureOIDC) {
        this.newSSOConfiguration.setProperties({
          oidcConfigurationUrl: this.oidcFormValues.azureConfigurationUrl,
          oidcClientId: this.oidcFormValues.clientId,
          oidcClientSecret: this.oidcFormValues.clientSecret,
        });
      } else {
        this.newSSOConfiguration.setProperties({
          oidcConfigurationUrl: this.oidcFormValues.genericConfigurationUrl,
          oidcClientId: this.oidcFormValues.clientId,
          oidcClientSecret: this.oidcFormValues.clientSecret,
        });
      }

      ssoConfiguration = this.newSSOConfiguration;
    }

    return ssoConfiguration;
  }

  disableSSOTask = task(async () => {
    try {
      await this.sso.disableSSO();

      this.showDisableSSOConfirmationModal = false;
      this.selectedSSOProvider = null;

      this.alerts.success('sso_disable.success', 'Disabled SSO on your account');
    } catch (err) {
      this.alerts.error('sso_disable.error', 'Something went wrong when disabling SSO, please try again');

      throw err;
    }
  });

  @action
  disableSSOStartAction() {
    if (this.ssoConfiguration == null) {
      return;
    }

    this.analytics.track('account', 'security_sso_settings_disable_started', {
      identityProvider: this.ssoConfiguration.identityProvider,
    });
    this.showDisableSSOConfirmationModal = true;
  }

  @action
  startUpdateClientSecret() {
    this.updateClientSecret = true;

    this.oidcFormValues = {
      ...this.oidcFormValues,
      clientSecret: '',
    };
    this.oidcValidations.validateAttribute('clientSecret');
  }

  @action
  cancelUpdateClientSecret() {
    this.setDefaults();
  }

  @action
  selectSSOProvider(identityProvider: IdentityProvider) {
    // this ID provider is already selected: don't do anything
    if (this.selectedSSOProvider === identityProvider) {
      return;
    }

    this.selectedSSOProvider = identityProvider;

    this.analytics.track('account', 'security_sso_settings_authenticate_started', {
      identityProvider: this.selectedSSOProvider as string,
    });

    this.newSSOConfiguration?.unloadRecord();
    this.newSSOConfiguration = this.store.createRecord('sso-configuration', { identityProvider });
    this.oidcFormValues = { ...DEFAULT_FORM_VALUES };
    this.oidcValidations.clearErrors();
  }

  @action
  cancelEditing() {
    this.ssoConfiguration?.rollbackAttributes();
    this.setDefaults();
  }

  @action onOktaOrgURLInput(value: string) {
    if (this.ssoConfiguration == null) {
      return;
    }

    let url = value.trim();

    // Org URL copy-pasted from Okta may be missing "https://" protocol prefix, let's add it:
    url = url !== '' && !/^https?:\/\//i.test(url) ? `https://${url}` : url;

    try {
      // parsing through URL constructor: removes trailing slash, potential "path" element (we only want protocol and hostname)
      // mostly defensive coding: customers shouldn't paste invalid organization URLs in the first place, if they follow our documentation for setup
      url = new URL(url).origin;
    } catch (e) {
      // invalid URL is entered: ignore
      // form validation will detect it, display proper error
    }

    this.oidcFormValues = { ...this.oidcFormValues, oktaOrganizationUrl: url };
    this.oidcValidations.validateAttribute('oktaOrganizationUrl');
  }

  @action onAzureConfigurationURLInput(value: string) {
    if (this.ssoConfiguration == null) {
      return;
    }

    let url = value.trim();

    try {
      // defensive coding: strip off everything except origin and pathname, like:
      // "https://login.microsoftonline.com/dd79f336-2e0c-44eb-ba17-a9f7405ab830/.well-known/openid-configuration"
      let urlOBJ = new URL(url);
      url = urlOBJ.origin + urlOBJ.pathname;
    } catch (e) {
      // invalid URL is entered: ignore
      // form validation will detect it, display proper error
    }

    this.oidcFormValues = { ...this.oidcFormValues, azureConfigurationUrl: url };
    this.oidcValidations.validateAttribute('azureConfigurationUrl');
  }

  @action onGenericConfigurationURLInput(value: string) {
    if (this.ssoConfiguration == null) {
      return;
    }

    let url = value.trim();

    // Org URL copy-pasted may be missing "https://" protocol prefix, let's add it:
    url = url !== '' && !/^https?:\/\//i.test(url) ? `https://${url}` : url;

    try {
      // defensive coding: strip off everything except origin and pathname, like:
      // "https://login.microsoftonline.com/dd79f336-2e0c-44eb-ba17-a9f7405ab830/.well-known/openid-configuration"
      let urlOBJ = new URL(url);
      url = urlOBJ.origin + urlOBJ.pathname;
    } catch (e) {
      // invalid URL is entered: ignore
      // form validation will detect it, display proper error
    }

    this.oidcFormValues = { ...this.oidcFormValues, genericConfigurationUrl: url };
    this.oidcValidations.validateAttribute('genericConfigurationUrl');
  }

  @action
  onOidcConfigChange(field: keyof typeof DEFAULT_FORM_VALUES, value: string) {
    this.oidcFormValues = { ...this.oidcFormValues, [field]: value };
    this.oidcValidations.validateAttribute(field);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'settings/security/sso': SettingsSecuritySSO;
  }
}
